<template>
  <div class="d-flex justify-start align-start">
    <Column
      v-for="(column, index) in columns"
      style="min-width: 340px"
      :tasks="getTasksByColumn(column.id)"
      :loadingTasks="column.loadingTasks"
      :loadingMoreTasks="column.loadingMoreTasks"
      :totalTasks="column.totalTasks"
      :column="column"
      :key="column.id"
      :class="{ 'me-4': index < 2 }"
      :data-status="column.id"
      @refresh="handleRefresh(column.id)"
      @dragged="handleDrag($event, column.id)"
      @taskCreated="handleColumnUpdate(column.id)"
      @getMoreTasks="getMoreTasks(column.id)"
    />
  </div>
</template>

<script>
import _ from 'lodash'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import Column from './Column.vue'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'
import { mapGetters } from 'vuex'

export default {
  name: 'TasksKanbanView',

  mixins: [mixin, trackerMixin],

  components: {
    Column,
  },

  data() {
    return {
      status: {
        open: { types: ['task'], status: ['open'] },
        inProgress: { types: ['task'], status: ['inProgress'] },
        completed: { types: ['task'], status: ['completed'] },
      },
      tasksByStatus: {
        open: [],
        inProgress: [],
        completed: [],
      },
      columns: [
        {
          name: this.$t('explore.entity_status.open'),
          id: 'open',
          page: 0,
          totalTasks: 0,
          loadingTasks: true,
          loadingMoreTasks: false,
        },
        {
          name: this.$t('explore.entity_status.in_progress'),
          id: 'inProgress',
          page: 0,
          totalTasks: 0,
          loadingTasks: true,
          loadingMoreTasks: false,
        },
        {
          name: this.$t('explore.entity_status.completed'),
          id: 'completed',
          page: 0,
          totalTasks: 0,
          loadingTasks: true,
          loadingMoreTasks: false,
        },
      ],
    }
  },

  created() {
    this.onTaskCreated = null
    this.onTaskUpdatedEvent = null
    this.onTaskDeleted = null
    this.onTaskDuplicated = null
    this.onTaskKanbanDeleted = null
    this.pendingColumnUpdateTimeouts = []
  },

  watch: {
    '$route.query': {
      immediate: true,
      async handler(newValue, oldValue) {
        const filters = [
          'assignees',
          'createdBy',
          'excludedAssignees',
          'site',
          'sort',
        ]
        const areFiltersEqual = _.isEqual(
          _.pick(newValue, filters),
          _.pick(oldValue, filters)
        )
        const termsChange = newValue?.searchTerms !== oldValue?.searchTerms
        if (!oldValue || !areFiltersEqual || termsChange) {
          this.filters = this.buildRouteFilters(newValue)
          this.resetColumns()
        }
      },
    },
  },

  computed: {
    ...mapGetters(['organizationId', 'organization']),

    getTasksByColumn() {
      return columnId => this.tasksByStatus[columnId]
    },
  },

  async mounted() {
    this.onTaskCreated = taskCreated => {
      this.handleColumnUpdate(taskCreated.status)
    }

    this.onTaskDeleted = task => {
      this.handleColumnUpdate(task.status)
    }

    this.onTaskKanbanDeleted = columnId => {
      this.handleColumnUpdate(columnId)
    }

    this.onTaskDuplicated = task => {
      this.handleColumnUpdate(task.status)
    }

    this.onTaskUpdatedEvent = async task => {
      if (!task?.id) return

      const isKanbanDragEvent = !task?.status

      if (isKanbanDragEvent) {
        await this.updateTask(task)
      }

      this.handleColumnUpdate(task.oldStatus)
      this.handleColumnUpdate(task.newStatus)
    }

    eventHub.$on(HUB_EVENTS.TASK_CREATION_DRAWER.CREATED, this.onTaskCreated)
    eventHub.$on(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATED, this.onTaskUpdatedEvent)
    eventHub.$on(HUB_EVENTS.TASK_EDITION_DRAWER.DELETED, this.onTaskDeleted)
    eventHub.$on(HUB_EVENTS.TASK_KANBAN.UPDATED, this.onTaskUpdatedEvent)
    eventHub.$on(HUB_EVENTS.TASK_KANBAN.DELETED, this.onTaskKanbanDeleted)
    eventHub.$on(HUB_EVENTS.TASK_KANBAN.DUPLICATED, this.onTaskDuplicated)
  },

  methods: {
    handleRefresh(column) {
      this.resetColumnContent(column)
      this.trackTasks('tasks.kanbanRefreshColumn', {
        column,
      })
    },

    resetColumns() {
      this.resetColumnContent('open')
      this.resetColumnContent('inProgress')
      this.resetColumnContent('completed')
    },

    async getMoreTasks(status) {
      const currentColumn = this.columns.find(col => col.id === status)

      if (this.tasksByStatus[status].length < currentColumn.totalTasks) {
        const newTasks = await this.getTasks(status)
        this.tasksByStatus[status].push(...newTasks)
      }
    },

    async updateTask(task) {
      try {
        if (
          !Object.values(this.$constants.tasksStatus).includes(task.newStatus)
        )
          return

        await api.tasks.update(this.organizationId, task.id, {
          status: task.newStatus,
        })
        this.showSuccess(this.$t('tasks.updated_successfully'))
      } catch (err) {
        this.handleRequestError(err)
        this.resetColumnContent(task.oldStatus)
        this.resetColumnContent(task.newStatus)
      }
    },

    async handleDrag(event, oldStatus) {
      const taskId = event?.item?.firstChild?.id

      const newStatus = event.to.getAttribute('data-status')

      const oldIndex = event.realOldIndex

      const newIndex = event.realNewIndex

      if (newStatus === oldStatus || !newStatus) return

      this.updateTask({ id: taskId, newStatus, oldIndex, oldStatus })

      const draggedItem = this.tasksByStatus[oldStatus].splice(oldIndex, 1)[0]
      draggedItem.status = newStatus

      this.tasksByStatus[newStatus].splice(newIndex, 0, draggedItem)

      const currentColumn = this.columns.find(col => col.id === newStatus)
      const oldColumn = this.columns.find(col => col.id === oldStatus)

      currentColumn.totalTasks = currentColumn.totalTasks + 1
      oldColumn.totalTasks = oldColumn.totalTasks - 1

      this.trackTasks('tasks.kanbanDragCard', {
        from: oldStatus,
        to: newStatus,
      })
    },

    buildRouteFilters(routeQuery) {
      return {
        assignees:
          _.isArray(routeQuery.assignees) || !routeQuery.assignees
            ? routeQuery.assignees
            : _.compact([routeQuery.assignees]),
        excludedAssignees:
          _.isArray(routeQuery.excludedAssignees) ||
          !routeQuery.excludedAssignees
            ? routeQuery.excludedAssignees
            : [routeQuery.excludedAssignees],
        createdBy: routeQuery.createdBy,
        sites:
          _.isArray(routeQuery.site) || !routeQuery.site
            ? routeQuery.site
            : [routeQuery.site],
      }
    },

    async getTasks(columnId) {
      const currentColumn = this.columns.find(col => col.id === columnId)

      try {
        if (this.tasksByStatus[columnId].length) {
          currentColumn.loadingMoreTasks = true
        } else {
          currentColumn.loadingTasks = true
        }
        const [by, order] = this.$route.query?.sort?.split('-')
        const response = await api.tasks.getAll({
          organizationId: this.organizationId,
          limit: 20,
          from: currentColumn.page * 20,
          sort: { by, order },
          filters: {
            ...this.filters,
            ...this.status[columnId],
          },
          terms: this.$route.query?.searchTerms ?? null,
          projections: [
            'name',
            'organization',
            'status',
            'createdAt',
            'createdBy',
            'assignees',
            'dueDate',
            'priority',
            'site',
            'description',
            'owner',
            'sites',
            'author',
            'linkedEntityType',
            'linkedEntityId',
            'isPrivate',
          ],
          expansions: [
            'name',
            'organization',
            'status',
            'createdAt',
            'createdBy',
            'assignees',
            'dueDate',
            'priority',
            'site',
            'description',
            'owner',
            'sites',
            'author',
            'linkedEntityType',
            'linkedEntityId',
          ],
        })

        currentColumn.page = currentColumn.page + 1
        currentColumn.totalTasks = response.data.totalRecordsInData
        return response.data.results
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        currentColumn.loadingMoreTasks = false
        currentColumn.loadingTasks = false
      }
    },

    async resetColumnContent(columnId) {
      const currentColumn = this.columns.find(col => col.id === columnId)
      currentColumn.page = 0
      currentColumn.totalTasks = 0
      currentColumn.loadingTasks = true

      currentColumn.loadingMoreTasks = false
      this.tasksByStatus[columnId] = []
      this.tasksByStatus[columnId] = await this.getTasks(columnId)
    },

    handleColumnUpdate(columnId) {
      const timeoutId = setTimeout(() => {
        this.pendingColumnUpdateTimeouts = this.pendingColumnUpdateTimeouts.filter(
          currentTimeoutId => currentTimeoutId !== timeoutId
        )

        this.resetColumnContent(columnId)
      }, 500)

      this.pendingColumnUpdateTimeouts.push(timeoutId)
    },

    clearPendingColumnUpdateTimeouts() {
      this.pendingColumnUpdateTimeouts.forEach(timeoutId => {
        clearTimeout(timeoutId)
      })

      this.pendingColumnUpdateTimeouts = []
    },
  },

  beforeDestroy() {
    this.clearPendingColumnUpdateTimeouts()
    eventHub.$off(HUB_EVENTS.TASK_CREATION_DRAWER.CREATED, this.onTaskCreated)
    eventHub.$off(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATED, this.onTaskUpdatedEvent)
    eventHub.$off(HUB_EVENTS.TASK_EDITION_DRAWER.DELETED, this.onTaskDeleted)
    eventHub.$off(HUB_EVENTS.TASK_KANBAN.UPDATED, this.onTaskUpdatedEvent)
    eventHub.$off(HUB_EVENTS.TASK_KANBAN.DUPLICATED, this.onTaskDuplicated)
    eventHub.$off(HUB_EVENTS.TASK_KANBAN.DELETED, this.onTaskKanbanDeleted)
  },
}
</script>
